/**
 * MISMO 3.4 RELATIONSHIPS parsing.
 *
 * In MISMO, ownership is NOT recorded on the record itself. An <ASSET> element
 * carries no borrower reference at all — the only place that says "this account
 * belongs to Borrower 2" is a <RELATIONSHIP> arc in the DEAL/RELATIONSHIPS
 * block:
 *
 *   <RELATIONSHIP xlink:arcrole="...ASSET_IsAssociatedWith_ROLE"
 *                 xlink:from="ASSET_121" xlink:to="BORROWER_21" />
 *
 * The same mechanism links liabilities to borrowers, and links an income item
 * to the employer that pays it. Without reading this block there is no way to
 * know who owns what, which is why the importer used to assign everything to
 * Borrower 1.
 *
 * The relationships block appears at the END of the DEAL, after the records it
 * refers to, so we pre-scan it before the main import traversal begins.
 */

// Arc roles we care about, matched on the trailing name so we aren't brittle
// about the urn prefix version.
const ASSET_TO_ROLE = "ASSET_IsAssociatedWith_ROLE";
const LIABILITY_TO_ROLE = "LIABILITY_IsAssociatedWith_ROLE";
const INCOME_TO_EMPLOYER = "CURRENT_INCOME_ITEM_IsAssociatedWith_EMPLOYER";
const ASSET_TO_LIABILITY = "ASSET_IsAssociatedWith_LIABILITY";

/**
 * Read an attribute off a node produced by xmlbuilder2's object format, which
 * prefixes attributes with "@". Falls back to the bare name so this keeps
 * working if the converter options ever change.
 */
export function attr(obj, name) {
  if (!obj || typeof obj !== "object") return undefined;
  return obj[`@${name}`] !== undefined ? obj[`@${name}`] : obj[name];
}

/** True if this PARTY carries a Borrower role. */
export function isBorrowerParty(party) {
  if (!party || !party.ROLES) return false;
  let roles = party.ROLES.ROLE;
  if (roles === undefined) return false;
  if (!Array.isArray(roles)) roles = [roles];
  return roles.some(
    (role) =>
      role &&
      (role.BORROWER !== undefined ||
        (role.ROLE_DETAIL &&
          role.ROLE_DETAIL.PartyRoleType === "Borrower"))
  );
}

/**
 * Map each borrower's ACTUAL role label to its zero-based position, read from
 * the file's PARTIES in document order.
 *
 * This replaces arithmetic on the label. Different systems number borrowers
 * differently — LANIS/POINT use BORROWER_11, BORROWER_21; real DU exports
 * (Epic) use BORROWER_1, BORROWER_2 — so decoding the number is ambiguous and
 * was silently dropping ownership on real LOS files. The label-to-position map
 * is exact for any scheme, and it matches the order the PARTY importer builds
 * document.applicant.borrowers.
 */
export function buildBorrowerLabelMap(dealObj) {
  const map = {};
  if (!dealObj || !dealObj.PARTIES) return map;

  let parties = dealObj.PARTIES.PARTY;
  if (parties === undefined) return map;
  if (!Array.isArray(parties)) parties = [parties];

  let index = 0;
  for (const party of parties) {
    if (!isBorrowerParty(party)) continue;

    let roles = party.ROLES.ROLE;
    if (!Array.isArray(roles)) roles = [roles];
    const borrowerRole = roles.find(
      (role) =>
        role &&
        (role.BORROWER !== undefined ||
          (role.ROLE_DETAIL &&
            role.ROLE_DETAIL.PartyRoleType === "Borrower"))
    );
    const label = borrowerRole && attr(borrowerRole, "xlink:label");
    if (label) map[label] = index;
    index++;
  }
  return map;
}

/**
 * Build lookup maps from the DEAL's RELATIONSHIPS block.
 *
 * Returns:
 *   assetOwners    label -> [borrower indices]   e.g. "ASSET_121" -> [0]
 *   liabilityOwners label -> [borrower indices]  e.g. "LIABILITY_x" -> [0,1] (joint)
 *   incomeEmployer label -> employer label  e.g. "CURRENT_INCOME_ITEM_1111" -> "EMPLOYER_111"
 */
export function parseRelationships(dealObj) {
  // assetOwners / liabilityOwners map a record label to the LIST of borrower
  // indices arced to it. A joint debt/asset has two (or more) arcs, so we must
  // collect ALL of them — keeping only the last (the old behavior) silently
  // dropped joint ownership.
  const maps = {
    assetOwners: {},
    liabilityOwners: {},
    incomeEmployer: {},
    // Property(asset) label -> [liability labels] it secures.
    assetLiabilities: {}
  };

  const pushOwner = (bag, key, index) => {
    if (index === null) return;
    if (!bag[key]) bag[key] = [];
    if (!bag[key].includes(index)) bag[key].push(index);
  };

  const borrowerIndexOf = buildBorrowerLabelMap(dealObj);
  const resolve = (label) =>
    label in borrowerIndexOf ? borrowerIndexOf[label] : null;

  const container = dealObj && dealObj.RELATIONSHIPS;
  if (!container) return maps;

  let rels = container.RELATIONSHIP;
  if (rels === undefined) return maps;
  if (!Array.isArray(rels)) rels = [rels];

  for (const rel of rels) {
    const arcrole = attr(rel, "xlink:arcrole");
    const from = attr(rel, "xlink:from");
    const to = attr(rel, "xlink:to");

    if (!arcrole || !from || !to) continue;

    if (arcrole.endsWith(ASSET_TO_ROLE)) {
      pushOwner(maps.assetOwners, from, resolve(to));
    } else if (arcrole.endsWith(LIABILITY_TO_ROLE)) {
      pushOwner(maps.liabilityOwners, from, resolve(to));
    } else if (arcrole.endsWith(INCOME_TO_EMPLOYER)) {
      maps.incomeEmployer[from] = to;
    } else if (arcrole.endsWith(ASSET_TO_LIABILITY)) {
      // from = property (ASSET) label, to = LIABILITY label
      if (!maps.assetLiabilities[from]) maps.assetLiabilities[from] = [];
      if (!maps.assetLiabilities[from].includes(to)) {
        maps.assetLiabilities[from].push(to);
      }
    }
  }

  // Keep each owner list in borrower order so owners[0] is the primary.
  Object.values(maps.assetOwners).forEach((a) => a.sort((x, y) => x - y));
  Object.values(maps.liabilityOwners).forEach((a) => a.sort((x, y) => x - y));

  return maps;
}
